import { useState, useRef } from 'react';
import * as styles from '../styles/components/resize-element-dialog.css.ts';
import type { ReportElementUnion } from '../../types/report.types';
import type { AspectRatioPreset } from '../../types/graph-builder.types';
import { Dialog } from './common/Dialog';
import { Button } from './common/Button';
import { REPORT_ELEMENT_SIZING } from '../constants/reportElementSizing';

const ASPECT_RATIO_OPTIONS: { value: AspectRatioPreset; label: string }[] = [
  { value: '16:9', label: '16:9 (Wide)' },
  { value: '4:3', label: '4:3 (Standard)' },
  { value: '1:1', label: '1:1 (Square)' },
  { value: '3:4', label: '3:4 (Portrait)' },
  { value: '9:16', label: '9:16 (Tall)' },
  { value: 'custom', label: 'Custom Height' },
];

interface ResizeElementDialogProps {
  element: ReportElementUnion;
  onResize: (width: 'fit' | number, height: number, aspectRatio: AspectRatioPreset) => void;
  onClose: () => void;
}

export function ResizeElementDialog({ element, onResize, onClose }: ResizeElementDialogProps) {
  const currentWidth = element.width ?? REPORT_ELEMENT_SIZING.DEFAULT_WIDTH;
  const currentHeight = element.height ?? REPORT_ELEMENT_SIZING.DEFAULT_HEIGHT;
  // 既存要素で aspectRatio 未設定の場合は 'custom' として扱う（後方互換性）
  const currentAspectRatio = element.aspectRatio ?? 'custom';

  const [widthMode, setWidthMode] = useState<'fit' | 'custom'>(
    currentWidth === 'fit' ? 'fit' : 'custom'
  );
  const [aspectRatio, setAspectRatio] = useState<AspectRatioPreset>(currentAspectRatio);
  const [widthError, setWidthError] = useState<string>('');
  const [heightError, setHeightError] = useState<string>('');

  const widthInputRef = useRef<HTMLInputElement>(null);
  const heightInputRef = useRef<HTMLInputElement>(null);

  const handleSave = () => {
    setWidthError('');
    setHeightError('');

    let hasError = false;
    let finalWidth: 'fit' | number = 'fit';
    let finalHeight = currentHeight;

    if (widthMode === 'custom' && widthInputRef.current) {
      const widthValue = Number(widthInputRef.current.value);
      if (isNaN(widthValue) || widthValue < REPORT_ELEMENT_SIZING.MIN_WIDTH) {
        setWidthError(`Width must be at least ${REPORT_ELEMENT_SIZING.MIN_WIDTH}px`);
        hasError = true;
      } else {
        finalWidth = widthValue;
      }
    }

    // aspectRatio が 'custom' の場合のみ height をバリデート
    if (aspectRatio === 'custom' && heightInputRef.current) {
      const heightValue = Number(heightInputRef.current.value);
      if (isNaN(heightValue) || heightValue < REPORT_ELEMENT_SIZING.MIN_HEIGHT) {
        setHeightError(`Height must be at least ${REPORT_ELEMENT_SIZING.MIN_HEIGHT}px`);
        hasError = true;
      } else {
        finalHeight = heightValue;
      }
    }

    if (hasError) return;

    onResize(finalWidth, finalHeight, aspectRatio);
    onClose();
  };

  const handleWidthModeChange = (mode: 'fit' | 'custom') => {
    setWidthMode(mode);
    setWidthError('');
  };

  const handleAspectRatioChange = (ratio: AspectRatioPreset) => {
    setAspectRatio(ratio);
    setHeightError('');
  };

  return (
    <Dialog
      isOpen={true}
      onClose={onClose}
      title="Resize Element"
      width="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            data-testid="save-resize-button"
          >
            Apply
          </Button>
        </>
      }
    >
      {/* Width settings */}
      <div className={styles.section}>
        <label className={styles.label}>Width</label>

        <div className={styles.radioGroup}>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              name="widthMode"
              value="fit"
              checked={widthMode === 'fit'}
              onChange={() => handleWidthModeChange('fit')}
              className={styles.radio}
            />
            <span>Fit to tab width</span>
          </label>

          <label className={styles.radioLabel}>
            <input
              type="radio"
              name="widthMode"
              value="custom"
              checked={widthMode === 'custom'}
              onChange={() => handleWidthModeChange('custom')}
              className={styles.radio}
            />
            <span>Custom width (px)</span>
          </label>
        </div>

        {widthMode === 'custom' && (
          <>
            <div className={styles.inputGroup}>
              <input
                ref={widthInputRef}
                type="number"
                min={REPORT_ELEMENT_SIZING.MIN_WIDTH}
                defaultValue={typeof currentWidth === 'number' ? currentWidth : REPORT_ELEMENT_SIZING.DEFAULT_CUSTOM_WIDTH}
                onChange={() => setWidthError('')}
                className={styles.input}
                data-testid="custom-width-input"
              />
              <span className={styles.unit}>px</span>
            </div>
            {widthError && (
              <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                {widthError}
              </div>
            )}
          </>
        )}
      </div>

      {/* Aspect Ratio settings */}
      <div className={styles.section}>
        <label className={styles.label} htmlFor="aspect-ratio-select">
          Aspect Ratio
        </label>
        <select
          id="aspect-ratio-select"
          value={aspectRatio}
          onChange={(e) => handleAspectRatioChange(e.target.value as AspectRatioPreset)}
          className={styles.select}
          data-testid="aspect-ratio-select"
        >
          {ASPECT_RATIO_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Height settings - only shown when aspectRatio is 'custom' */}
      {aspectRatio === 'custom' && (
        <div className={styles.section}>
          <label className={styles.label} htmlFor="height-input">
            Height (px)
          </label>
          <div className={styles.inputGroup}>
            <input
              ref={heightInputRef}
              id="height-input"
              type="number"
              min={REPORT_ELEMENT_SIZING.MIN_HEIGHT}
              defaultValue={currentHeight}
              onChange={() => setHeightError('')}
              className={styles.input}
              data-testid="height-input"
            />
            <span className={styles.unit}>px</span>
          </div>
          {heightError && (
            <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
              {heightError}
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}
